
import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { Observable, Subject, Subscription, distinctUntilChanged, map, skip } from 'rxjs';
import { AuthService } from '../../user/auth.service';
import { EssErrorService } from 'ngx-essentia';
import { isNetworkRequestFailed } from './poll-errors';
import { AccessGrantService, EDIT_ROLES, MANAGE_ROLES, PollRole, pollGrantId } from './access-grant.service';

/** a role change on the open poll (workorder-poll-scoped-access-1-6, task 1) - `cause` is the trigger as logged */
export interface RoleChange { from: PollRole; to: PollRole; cause: string }


@Injectable({
    providedIn: 'root'
})
/**
 * Service that holds the current ownerId and pollId which are set with initService()
 * Components can use changeIndicator to get informed if the current poll has changed
 * Use this service to get the paths to the DB and the URLs
 */

export class CurrentPollService {
    private _ownerId: string;
    private _surveyId: string;
    private _voteUserId: string;
    public _isInitialized = false;
    /** the role on the current poll (workorder-poll-scoped-access-1-2, task 4a) - 'none' before setCurrentPoll */
    private _role: PollRole = 'none';
    private _isSampleMode = false;
    /** invalidates an older setCurrentPoll / re-evaluation that is still awaiting the role when the poll changes */
    private roleSeq = 0;
    private keyWatcher: Subscription | null = null;
    /** the role the key watcher listens for - editor (editKey) and co-owner (ownerKey) watch different paths */
    private keyWatcherRole: PollRole | null = null;
    private grantSubscription: Subscription | null = null;
    /**
     * "{ownerId}/{pollId}" of the polls deleted in this session - by PollService.deletePoll or noticed by the delete
     * watch of HeaderService. Survives unInitializeCurrentPoll on purpose: the auto-save of the vote preview fires from
     * ngOnDestroy after the delete, and a full header write onto the deleted path would bring the poll back as a header
     * without options (workorder-header-persist-and-poll-init-denied-with-user-2026-11-03, task 0)
     */
    private readonly deletedPolls = new Set<string>();
    private readonly roleChangedSubject = new Subject<RoleChange>();
    /**
     * Emits when the role on the OPEN poll changes while it stays open (auth change, claims change, grant
     * change, key revoked) - never for the first resolution of setCurrentPoll and never on a poll switch.
     * The context action bar leaves the owner page on it (workorder 1.6 task 1); the writes themselves
     * stay guarded by the rules.
     */
    public readonly roleChanged$: Observable<RoleChange> = this.roleChangedSubject.asObservable();
    constructor(
        private logger: LoggerService,
        private authService: AuthService,
        private errorService: EssErrorService,
        private accessGrantService: AccessGrantService,
    ) {
        this.subscribeAuthChanges();
    }

    /**
     * `_role` is a snapshot taken in setCurrentPoll. A logout, a login with another account, an account
     * merge or a claim withdrawn after a secretKey rotation changed the right to write while the poll
     * stayed open - the UI kept offering the owner editing and the write ended in PERMISSION_DENIED
     * (workorder-auth-window-option-access-link-2026-10-04, task 7). Re-evaluated on every uid change
     * and on every claims change, as long as a poll is initialized; since 1.2 also when the own grant
     * on the current poll changes (accepted, removed, key updated) and when the key behind a grant role
     * is rotated (watchPollKey).
     */
    private subscribeAuthChanges(): void {
        this.authService.afAuthUser$.pipe(
            map(user => user?.uid ?? null),
            distinctUntilChanged()
        ).subscribe(uid => { void this.reevaluateRole('auth change', uid); });
        this.authService.claimsChanged.subscribe(() => { void this.reevaluateRole('claims change', this.authService.uid); });
    }

    private async reevaluateRole(cause: string, uid: string | null): Promise<void> {
        if (!this._isInitialized) {
            return;
        }
        if (cause === 'auth change') {
            // the auth state emits before the AuthService has read the new user's token - the cached
            // token of the signed-in user, no round trip (claim changes arrive via claimsChanged)
            try {
                await this.authService.loadIdTokenResult();
            } catch (error) {
                // background call, nobody clicked anything - isOwner keeps its value
                if (isNetworkRequestFailed(error)) {
                    this.logger.warn('CurrentPollService: token not loaded after auth change, no connection', error?.code);
                } else {
                    this.logger.error('CurrentPollService: token not loaded after auth change', error);
                }
                return;
            }
            if (uid !== (this.authService.uid ?? null)) {
                return; // a newer auth state arrived while awaiting - it does its own re-evaluation
            }
        }
        if (!this._isInitialized) {
            return;
        }
        const seq = this.roleSeq;
        const role = uid ? await this.resolveRole() : 'none';
        if (seq !== this.roleSeq || !this._isInitialized) {
            return; // the poll changed while awaiting - the new setCurrentPoll resolved its own role
        }
        this.applyRole(role, cause);
    }

    /** the role from the AccessGrantService; a failed read (offline) leaves the user without rights, never throws here */
    private async resolveRole(): Promise<PollRole> {
        try {
            return await this.accessGrantService.resolvePollRole(this._ownerId, this._surveyId);
        } catch (error) {
            if (globalThis.navigator?.onLine === false || isNetworkRequestFailed(error)) {
                this.logger.warn('CurrentPollService: role not resolved, no connection', this._ownerId, this._surveyId, error?.code ?? error?.message);
            } else {
                this.logger.error('CurrentPollService: role not resolved. ownerId: ' + this._ownerId + '; pollId: ' + this._surveyId, error);
            }
            return 'none';
        }
    }

    private applyRole(role: PollRole, cause: string): void {
        if (role !== this._role) {
            this.changeRole(role, cause);
        }
        this.syncKeyWatcher();
    }

    /** the one place a role flips on an open poll: warning line, new value, roleChanged$ */
    private changeRole(to: PollRole, cause: string): void {
        const from = this._role;
        this.logger.warn(`CurrentPollService: role changed after ${cause}`, from, to);
        this._role = to;
        this.roleChangedSubject.next({ from, to, cause });
    }

    /**
     * Workorder 1.2 rule 6: a co-owner / editor keeps a listen on the key behind his role. A rotated key
     * or a revoked listen takes the role away right away - like a claims change for a legacy delegate.
     */
    private syncKeyWatcher(): void {
        const role = this._role;
        if (this.keyWatcher && this.keyWatcherRole === role) {
            return; // already watching the right key path of this poll
        }
        this.keyWatcher?.unsubscribe();
        this.keyWatcher = null;
        this.keyWatcherRole = null;
        if (role !== 'coowner' && role !== 'editor') {
            return;
        }
        const ownerId = this._ownerId;
        const pollId = this._surveyId;
        this.keyWatcherRole = role;
        this.keyWatcher = this.accessGrantService.watchPollKey(ownerId, pollId, role).subscribe(state => {
            if (state !== 'revoked' || !this.isCurrentPoll(ownerId, pollId)) return;
            this.keyWatcher?.unsubscribe();
            this.keyWatcher = null;
            this.keyWatcherRole = null;
            this.accessGrantService.invalidate(ownerId, pollId);
            if (this._role !== 'none') {
                this.changeRole('none', 'key revoked');
            }
        });
    }

    /** true when the context is initialized and is exactly this poll */
    public isCurrentPoll(ownerId: string, pollId: string): boolean {
        return this._isInitialized && ownerId === this._ownerId && pollId === this._surveyId;
    }

    /** re-evaluates when the own grant on the current poll changes (accepted, removed, key updated) */
    private subscribeGrantChanges(ownerId: string, pollId: string): void {
        this.grantSubscription?.unsubscribe();
        this.grantSubscription = this.accessGrantService.myPollGrants$.pipe(
            map(grants => grants[pollGrantId(ownerId, pollId)]?.key ?? null),
            distinctUntilChanged(),
            skip(1) // the state setCurrentPoll resolves with
        ).subscribe(() => {
            if (!this.isCurrentPoll(ownerId, pollId)) return;
            // the watcher is bound to the old grant key - a new resolution sets a new one
            this.keyWatcher?.unsubscribe();
            this.keyWatcher = null;
            this.keyWatcherRole = null;
            void this.reevaluateRole('grant change', this.authService.uid ?? null);
        });
    }


    /**
     * Checks if the provided poll identifiers match the poll currently managed by the service.
     * This is useful to avoid re-initializing the service if the context has not changed.
     * @param ownerId - The UID of the poll's owner to check against the current context.
     * @param pollId - The ID of the poll to check against the current context.
     * @param voteUserId - (Optional) The UID of the user whose vote is being accessed (for delegated votes).
     * @returns `true` if the provided identifiers match the currently set poll, otherwise `false`.
     */
    getIsCurrentPoll(ownerId: string, pollId: string, voteUserId?: string): boolean {
        return ownerId === this._ownerId && pollId === this._surveyId && voteUserId === this._voteUserId
    }

    /**
     * Sets the ownerId and pollId for future calls and reinits all services that have registred with registerInitCallBack
     * Can be called also with empty pollId and OwnerIdto init the other services
     * @param ownerId UID of the owvner of the service
     * @param pollId poll id or index
     */
    public async setCurrentPoll(ownerId: string, pollId: string, voteUserId?: string): Promise<boolean> {
        if (this.getIsCurrentPoll(ownerId, pollId, voteUserId)) return false
        this.logger.log('CurrentPollService: setCurrentPoll: ', ownerId, ' ', pollId, voteUserId);
        if (!ownerId || !pollId) throw (this.errorService.newError('setCurrentPoll error: ' + ownerId + ' ' + pollId))
        this.unInitializeCurrentPoll();
        if (!ownerId || !pollId || ownerId === '' || pollId === '') {
            this.logger.log('CurrentPollService:initService: ownerId and surveId should not be empty');
        }

        this._ownerId = ownerId;
        this._surveyId = pollId;
        this._voteUserId = voteUserId;
        this._isInitialized = true;
        const seq = ++this.roleSeq;
        const uid = await this.authService.getUid()
        const role = uid ? await this.resolveRole() : 'none';
        if (seq !== this.roleSeq) {
            return true; // another setCurrentPoll took over while awaiting
        }
        this._role = role;
        this.syncKeyWatcher();
        this.subscribeGrantChanges(ownerId, pollId);
        this.logger.log('CurrentPollService: initService: ', this);
        return true
    }

    public unInitializeCurrentPoll() {
        this.logger.log('unInitializeCurrentPoll ', this);
        this.roleSeq++;
        this.keyWatcher?.unsubscribe();
        this.keyWatcher = null;
        this.keyWatcherRole = null;
        this.grantSubscription?.unsubscribe();
        this.grantSubscription = null;
        this._ownerId = null;
        this._surveyId = null;
        this._voteUserId = null;
        this._isInitialized = false;
        this._role = 'none';
        this._isSampleMode = false;
    }

    /**
     * Sample-Mode: der aktuelle Poll wird als Demo (Sample Poll) angezeigt.
     * Auswirkung: Save-Aktionen schreiben NICHT in die RTDB, stattdessen wird
     * ein 'Demo — kopier den Poll'-Dialog gezeigt (siehe VotesValidationService).
     */
    public setSampleMode(value: boolean) {
        this._isSampleMode = value;
    }

    get isSampleMode(): boolean {
        return this._isSampleMode;
    }

    /** from now on every save of this poll is dropped (see deletedPolls) */
    public markDeleted(ownerId: string, pollId: string): void {
        this.deletedPolls.add(ownerId + '/' + pollId);
    }

    /** the delete did not go through - the poll is editable again */
    public unmarkDeleted(ownerId: string, pollId: string): void {
        this.deletedPolls.delete(ownerId + '/' + pollId);
    }

    public isDeleted(ownerId: string, pollId: string): boolean {
        return !!ownerId && !!pollId && this.deletedPolls.has(ownerId + '/' + pollId);
    }

    /** the initialized poll was deleted in this session */
    get isCurrentPollDeleted(): boolean {
        return this._isInitialized && this.isDeleted(this._ownerId, this._surveyId);
    }

    /**
     * Returns the ownerId
     * Might be called before init hence it return null
     */
    get ownerId() {
        return this._ownerId;
    }

    /**
     * Returns the pollId
     * Might be called before init hence it return null
     */
    get pollId() {
        return this._surveyId;
    }

    /**
     * Returns the voteUserId
     * Is neede if you want to access a users vote that was delegated
     */
    get voteUserId() {
        return this._voteUserId;
    }
    get isInitialized() {
        return this._isInitialized;
    }

    get changeIndicator(): string {
        if (this.pollId) { // we don't want to indicate in the begninning when pollId is still empty
            return this.ownerId + this.pollId;
        }
        return null;
    }


    /** the role on the current poll: owner | legacy | coowner | editor | stale | none */
    get role(): PollRole {
        return this._role;
    }

    /**
     * Alias with the semantics "may edit the poll" (owner, legacy delegate, co-owner; editor only from old
     * dev data since 1.9) - the name stays so the templates keep working; a grant holder is an "owner" in
     * this sense. Delete and the key ask `canManage`. The mail surfaces ask this alias too since 1.5 - the mail carries
     * the strongest key the user holds (NavigationService.getJoinLinkParts).
     */
    get isOwner(): boolean {
        return EDIT_ROLES.includes(this._role);
    }

    /** owner, legacy, co-owner: delete the poll, read its key (the organizer link) */
    get canManage(): boolean {
        return MANAGE_ROLES.includes(this._role);
    }

    public getPollId(pollId?: string): string {
        this.checkInitRequired(pollId);
        return pollId ? pollId : this.pollId;
    }

    public getOwnerId(ownerId?: string): string {
        this.checkInitRequired(ownerId);
        return ownerId ? ownerId : this.ownerId;
    }

    public getVoteUserId(voteUserId?: string): string {
        if (!this.authService.isLoggedIn) {
            this.checkInitRequired(voteUserId);
        }
        if (voteUserId) { return voteUserId; }
        if (this.voteUserId) { return this.voteUserId; }
        return this.authService.uid;
    }


    private checkInitRequired(value?: string) {
        if (!value) {
            if (!this.isInitialized) {
                this.logger.warn('CurrentPollService: checkInitRequired called before initialization');
            }
        }
    }


}
